import { Fragment, ReactNode } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { ConversationItem } from '../types/ConversationItem';
import { AtsCandidateLink } from 'services/integration/components/AtsCandidateLink';
import { ChatBubbleOutline, EventBusy, LocationOn } from '@mui/icons-material';
import { textChannelByChannelValue } from 'services/campaign/constants';
import { ConversationsNavigator } from './ConversationsNavigator';
import { ReviewIcon } from 'common/icons/ReviewIcon';
import { getScreeningStatusChipProps } from './ScreeningStatusChip';
import { hasMoreTimeRequested } from './ConversationStatusIconAndLabel';
import { conversationDetailsHeaderSx, conversationTagsSx } from '../styles';
import { CampaignTitleLink } from './CampaignTitleLink';
import { mapType } from 'utils/generatedCampaignMappers';
import { formatDateFromNowAbbreviated } from 'utils/datetime';

type Props = {
  conversation: ConversationItem;
  jobTitle?: string;
  location: string;
  moveToConversation: (direction: 'next' | 'previous') => void;
  conversationsCount: number;
  conversationIndex: number;
};

export type { Props as ConversationDetailsHeaderProps };

export const ConversationDetailsHeader: React.FC<Props> = ({
  conversation,
  jobTitle,
  location,
  moveToConversation,
  conversationsCount,
  conversationIndex
}) => {
  const channel = conversation.activeChannel || conversation.campaign?.channel;
  const showMoreTimeRequestedIcon = hasMoreTimeRequested(
    conversation.unresolvedNeedsReviewReasons
  );

  const statusChipProps = getScreeningStatusChipProps({
    status: conversation.status,
    campaignType: mapType(conversation.campaign?.type),
    meetingBookingStatus: conversation.meetingBookingStatus
  });
  const conversationLocation =
    location || conversation.campaign?.jobDescriptionParsed?.location;

  const metaItems: ReactNode[] = [
    statusChipProps && <Chip {...statusChipProps} />,
    jobTitle && (
      <CampaignTitleLink
        campaignId={conversation.campaignId}
        jobTitle={jobTitle}
      />
    ),
    <Typography>Created by {conversation.user?.name}</Typography>,
    conversationLocation && (
      <Box sx={conversationDetailsHeaderSx.metaItem}>
        <LocationOn />
        <Typography>{conversationLocation}</Typography>
      </Box>
    ),
    <Box sx={conversationDetailsHeaderSx.metaItem}>
      <ChatBubbleOutline />
      <Typography>
        {channel ? textChannelByChannelValue[channel] : 'Unknown'}
      </Typography>
    </Box>,
    conversation.updatedAt && (
      <Typography>
        {formatDateFromNowAbbreviated(conversation.updatedAt)}
      </Typography>
    )
  ].filter(Boolean);

  return (
    <Stack>
      <Stack sx={conversationDetailsHeaderSx.nameRow}>
        <Typography sx={conversationDetailsHeaderSx.name}>
          {conversation.participantFirstName} {conversation.participantLastName}
        </Typography>
        {showMoreTimeRequestedIcon ? (
          <Tooltip title="Candidate has requested additional time slots" arrow>
            <span>
              <EventBusy
                sx={{
                  fontSize: '20px',
                  color: (theme) => theme.palette.warning.dark
                }}
              />
            </span>
          </Tooltip>
        ) : (
          conversation.needsReview && (
            <Tooltip title="Review" arrow>
              <span>
                <ReviewIcon size={16} />
              </span>
            </Tooltip>
          )
        )}
      </Stack>
      <AtsCandidateLink
        atsCandidateId={conversation.atsCandidateId}
        atsApplicationId={conversation.atsApplicationId}
      />
      <Box sx={conversationDetailsHeaderSx.metaRow}>
        {metaItems.map((item, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <Typography
                aria-hidden
                sx={conversationDetailsHeaderSx.separator}
              >
                |
              </Typography>
            )}
            {item}
          </Fragment>
        ))}
      </Box>
      {(!!conversation.externalId || !!conversation.tags?.length) && (
        <Box sx={conversationTagsSx.drawerTags}>
          {/* Plain text, not a chip: it reads as the candidate's own id rather
              than another tag, while still sitting on the tag row. */}
          {conversation.externalId && (
            <Typography variant="body2">{conversation.externalId}</Typography>
          )}
          {conversation.tags?.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              color="primary"
              variant="inverse"
            />
          ))}
        </Box>
      )}
      <ConversationsNavigator
        moveToConversation={moveToConversation}
        conversationsCount={conversationsCount}
        conversationIndex={conversationIndex}
        conversation={conversation}
      />
    </Stack>
  );
};
